import React, { useState } from 'react';
import { supabase } from '../supabase';
import './PartnershipCollaboration.css';

const PartnershipCollaboration = () => {
  const [formData, setFormData] = useState({
    organizationName: '',
    legalStatus: '',
    coreServices: '',
    headquarters: '',
    country: '',
    organizationType: '',
    partnershipType: [],
    objectives: '',
    areasOfCollaboration: '',
    contributionType: [],
    contributionDetails: '',
    expectedBenefits: '',
    contactPerson: '',
    position: '',
    email: '',
    phone: '',
    website: '',
    agreeTerms: false
  });

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [formStatus, setFormStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      if (name === 'partnershipType' || name === 'contributionType') {
        const updated = checked
          ? [...formData[name], value]
          : formData[name].filter(item => item !== value);
        setFormData({ ...formData, [name]: updated });
      } else {
        setFormData({ ...formData, [name]: checked });
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };

  const uploadFiles = async (files) => {
    const uploadedUrls = [];
    let totalFiles = files.length;
    let uploadedCount = 0;

    for (const file of files) {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
      const filePath = `partnerships/${fileName}`;

      const { error } = await supabase.storage
        .from('partnership-documents')
        .upload(filePath, file);

      if (error) {
        console.error('Upload error:', error);
        continue;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('partnership-documents')
        .getPublicUrl(filePath);

      uploadedUrls.push(publicUrl);
      uploadedCount++;
      setUploadProgress(Math.round((uploadedCount / totalFiles) * 100));
    }

    return uploadedUrls;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFormStatus(null);
    setUploadProgress(0);

    try {
      // Upload files first
      let fileUrls = [];
      if (selectedFiles.length > 0) {
        fileUrls = await uploadFiles(selectedFiles);
      }

      // Convert arrays to comma-separated strings for Supabase
      const partnershipTypeStr = formData.partnershipType.join(', ');
      const contributionTypeStr = formData.contributionType.join(', ');

      const { error } = await supabase
        .from('partnership_inquiries')
        .insert([{
          organization_name: formData.organizationName,
          legal_status: formData.legalStatus,
          core_services: formData.coreServices,
          headquarters: formData.headquarters,
          country: formData.country,
          organization_type: formData.organizationType,
          partnership_type: partnershipTypeStr,
          objectives: formData.objectives,
          areas_of_collaboration: formData.areasOfCollaboration,
          contribution_type: contributionTypeStr,
          contribution_details: formData.contributionDetails,
          expected_benefits: formData.expectedBenefits,
          contact_person: formData.contactPerson,
          position: formData.position,
          email: formData.email,
          phone: formData.phone,
          website: formData.website,
          document_urls: fileUrls.join(', '),
          status: 'pending'
        }]);

      if (error) {
        console.error('Supabase Error:', error);
        throw error;
      }
      
      setFormStatus('success');
      setShowPopup(true);
      setFormData({
        organizationName: '',
        legalStatus: '',
        coreServices: '',
        headquarters: '',
        country: '',
        organizationType: '',
        partnershipType: [],
        objectives: '',
        areasOfCollaboration: '',
        contributionType: [],
        contributionDetails: '',
        expectedBenefits: '',
        contactPerson: '',
        position: '',
        email: '',
        phone: '',
        website: '',
        agreeTerms: false
      });
      setSelectedFiles([]);
      setUploadProgress(0);
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormStatus('error');
      setShowPopup(true);
    } finally {
      setLoading(false);
    }
  };

  const closePopup = () => {
    setShowPopup(false);
  };

  const partners = [
    { name: 'Presidency', logo: '/images/logo/presidency.png' },
    { name: 'Foreign Affairs', logo: '/images/logo/foreign-affairs.jpg' },
    { name: 'Ministry of Foreign Affairs', logo: '/images/logo/mofa.jpg' },
    { name: 'Afreximbank', logo: '/images/logo/afrexim.png' },
    { name: 'AfCFTA', logo: '/images/logo/afcfta.jpg' },
    { name: 'Zenith Bank', logo: '/images/logo/zenith-w-bg.jpeg' },
    { name: 'FCMB', logo: '/images/logo/fcmb.jpg' },
    { name: 'ICAD', logo: '/images/logo/icad.jpeg' },
    { name: 'UNDP', logo: '/images/logo/undp.jpg' },
    { name: 'SDG', logo: '/images/logo/sdg.png' },
    { name: 'ASPI', logo: '/images/logo/aspi.jpg' }
  ];

  const partnershipTypes = [
    'Technical', 'Financial', 'Strategic', 'Project Development',
    'Investment', 'Media', 'Exhibition', 'Knowledge', 'Institutional'
  ];

  const contributionTypes = [
    'Financial sponsorship',
    'Technical expertise',
    'Project pipeline development',
    'Capacity building',
    'Equipment or in-kind support',
    'Investment facilitation'
  ];

  return (
    <div>
      {/* Popup Modal */}
      {showPopup && (
        <div className="popup-overlay" onClick={closePopup}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            {formStatus === 'success' ? (
              <>
                <div className="popup-icon success">✅</div>
                <h2>Submission Successful!</h2>
                <p>Your partnership inquiry has been received.</p>
                <p className="popup-sub-text">Our team will contact you within 48 hours.</p>
                <button className="popup-btn" onClick={closePopup}>Close</button>
              </>
            ) : (
              <>
                <div className="popup-icon error">❌</div>
                <h2>Submission Failed</h2>
                <p>There was a problem submitting your inquiry.</p>
                <p className="popup-sub-text">Please try again or contact us directly.</p>
                <button className="popup-btn" onClick={closePopup}>Try Again</button>
              </>
            )}
          </div>
        </div>
      )}

      <section className="page-hero">
        <h1>Partnership & Collaboration</h1>
        <p>Join us in shaping Africa's sustainable future</p>
      </section>

      <section className="partnership-section">
        <div className="container">
          {/* Partnership Overview */}
          <div className="partnership-overview">
            <h2>Why Partner With Us?</h2>
            <p>
              ABVIG and the AICIS Global Summit offer unparalleled opportunities for organizations 
              to align with Africa's transformative journey. By partnering with us, you gain access 
              to a network of government leaders, global investors, and industry pioneers dedicated 
              to sustainable development and climate resilience.
            </p>
            
            <div className="partnership-benefits">
              <div className="benefit-item">
                <span className="benefit-icon">🌍</span>
                <h3>Global Visibility</h3>
                <p>Showcase your brand to over 3,000+ participants from 56 countries</p>
              </div>
              <div className="benefit-item">
                <span className="benefit-icon">🤝</span>
                <h3>Strategic Networking</h3>
                <p>Connect with government officials, investors, and industry leaders</p>
              </div>
              <div className="benefit-item">
                <span className="benefit-icon">💡</span>
                <h3>Impactful Collaboration</h3>
                <p>Drive meaningful change in infrastructure and climate action</p>
              </div>
              <div className="benefit-item">
                <span className="benefit-icon">📈</span>
                <h3>Investment Opportunities</h3>
                <p>Access $12T in investment opportunities across Africa</p>
              </div>
            </div>
          </div>

          {/* Current Partners */}
          <div className="partners-section">
            <h2 className="section-title">Our Current Partners</h2>
            <div className="partners-grid">
              {partners.map((partner, index) => (
                <div className="partner-item" key={index}>
                  <img src={partner.logo} alt={partner.name} />
                  <p>{partner.name}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Summit Highlight */}
          <div className="summit-highlight">
            <div className="summit-highlight-content">
              <h3>AICIS 2026 Global Summit</h3>
              <p>
                <strong>25-28 November 2026 • Abuja, Nigeria</strong>
              </p>
              <p>
                The Africa Infrastructure, Climate Change, Security & Green Investment Summit 
                is Africa's premier, policy-aligned investment platform. Join us for Presidential 
                Opening at Aso Rock Villa and Technical Sessions at Transcorp Hilton & Afreximbank AATC.
              </p>
              <a 
                href="https://aicis2026.abvig.com/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="btn-gold"
              >
                Learn More About AICIS 2026
              </a>
            </div>
          </div>

          {/* Partnership Form */}
          <div className="partnership-form-wrapper">
            <h2>Partnership & Collaboration Form</h2>
            <p className="form-intro">
              Complete the form below to express your interest in partnering with ABVIG and the AICIS Global Summit.
            </p>

            <form className="partnership-form" onSubmit={handleSubmit}>
              {/* Section 1: Organization Profile */}
              <div className="form-section">
                <h3>Section 1: Organization Profile</h3>
                
                <div className="form-group">
                  <label>1. Name of Organization *</label>
                  <input
                    type="text"
                    name="organizationName"
                    value={formData.organizationName}
                    onChange={handleChange}
                    required
                    placeholder="Enter your organization name"
                  />
                </div>

                <div className="form-group">
                  <label>2. Legal Status/Registration *</label>
                  <input
                    type="text"
                    name="legalStatus"
                    value={formData.legalStatus}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Limited Liability Company, NGO, Government Agency"
                  />
                </div>

                <div className="form-group">
                  <label>3. Core Services & Operational Experience *</label>
                  <textarea
                    name="coreServices"
                    value={formData.coreServices}
                    onChange={handleChange}
                    required
                    rows="3"
                    placeholder="Describe your organization's core services and operational experience"
                  />
                </div>

                <div className="form-group">
                  <label>4. Headquarters and Areas of Operation *</label>
                  <input
                    type="text"
                    name="headquarters"
                    value={formData.headquarters}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Abuja, Nigeria (with operations in West Africa)"
                  />
                </div>

                <div className="form-group">
                  <label>5. Country *</label>
                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Country</option>
                    <option value="Nigeria">Nigeria</option>
                    <option value="Ghana">Ghana</option>
                    <option value="South Africa">South Africa</option>
                    <option value="Kenya">Kenya</option>
                    <option value="Egypt">Egypt</option>
                    <option value="UK">United Kingdom</option>
                    <option value="USA">United States</option>
                    <option value="China">China</option>
                    <option value="India">India</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>6. Organization Type *</label>
                  <select
                    name="organizationType"
                    value={formData.organizationType}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Organization Type</option>
                    <option value="Private Sector">Private Sector</option>
                    <option value="Government Agency">Government Agency</option>
                    <option value="NGO / Civil Society">NGO / Civil Society</option>
                    <option value="Development Institution">Development Institution</option>
                    <option value="Investment Firm">Investment Firm</option>
                    <option value="Media / Press">Media / Press</option>
                    <option value="Education / Research">Education / Research</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              {/* Section 2: Partnership Interest */}
              <div className="form-section">
                <h3>Section 2: Partnership Interest</h3>

                <div className="form-group">
                  <label>7. Type of Partnership Proposed *</label>
                  <div className="checkbox-grid">
                    {partnershipTypes.map((type) => (
                      <label className="checkbox-label" key={type}>
                        <input
                          type="checkbox"
                          name="partnershipType"
                          value={type}
                          checked={formData.partnershipType.includes(type)}
                          onChange={handleChange}
                        />
                        {type}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>8. Objectives of the Proposed Collaboration *</label>
                  <textarea
                    name="objectives"
                    value={formData.objectives}
                    onChange={handleChange}
                    required
                    rows="3"
                    placeholder="What are your objectives for this partnership?"
                  />
                </div>
              </div>

              {/* Section 3: Areas of Collaboration */}
              <div className="form-section">
                <h3>Section 3: Areas of Collaboration</h3>

                <div className="form-group">
                  <label>9. Specific Programmes, Projects, Sectors, or Thematic Areas of Interest</label>
                  <textarea
                    name="areasOfCollaboration"
                    value={formData.areasOfCollaboration}
                    onChange={handleChange}
                    rows="3"
                    placeholder="List specific areas where you would like to collaborate"
                  />
                </div>

                <div className="form-group">
                  <label>10. Expected Contributions and Value Addition</label>
                  <textarea
                    name="contributionDetails"
                    value={formData.contributionDetails}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Describe the value you bring to this partnership"
                  />
                </div>
              </div>

              {/* Section 4: Proposed Contribution */}
              <div className="form-section">
                <h3>Section 4: Proposed Contribution</h3>

                <div className="form-group">
                  <label>11. Type of Contribution *</label>
                  <div className="checkbox-grid">
                    {contributionTypes.map((type) => (
                      <label className="checkbox-label" key={type}>
                        <input
                          type="checkbox"
                          name="contributionType"
                          value={type}
                          checked={formData.contributionType.includes(type)}
                          onChange={handleChange}
                        />
                        {type}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Section 5: Expected Benefits */}
              <div className="form-section">
                <h3>Section 5: Expected Benefits</h3>

                <div className="form-group">
                  <label>12. Mutual Benefits and Expected Outcomes</label>
                  <textarea
                    name="expectedBenefits"
                    value={formData.expectedBenefits}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Describe the mutual benefits you expect from this partnership"
                  />
                </div>
              </div>

              {/* Section 6: Contact Information */}
              <div className="form-section">
                <h3>Section 6: Contact Information</h3>

                <div className="form-row">
                  <div className="form-group">
                    <label>Contact Person *</label>
                    <input
                      type="text"
                      name="contactPerson"
                      value={formData.contactPerson}
                      onChange={handleChange}
                      required
                      placeholder="Full name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Position *</label>
                    <input
                      type="text"
                      name="position"
                      value={formData.position}
                      onChange={handleChange}
                      required
                      placeholder="Job title"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="email@organization.com"
                    />
                  </div>
                  <div className="form-group">
                    <label>Telephone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+234 800 000 0000"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Official Website</label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="www.organization.com"
                  />
                </div>
              </div>

              {/* Supporting Documents */}
              <div className="form-section">
                <h3>Supporting Documents</h3>
                <p className="supporting-docs-text">
                  Please attach the following documents (if applicable):
                </p>
                <ul className="supporting-docs-list">
                  <li>✓ Organizational profile</li>
                  <li>✓ Registration or statutory authorization</li>
                  <li>✓ Relevant certifications or accreditations</li>
                  <li>✓ Company capability statement</li>
                  <li>✓ Previous event references (optional)</li>
                </ul>
                <div className="form-group">
                  <label>Upload Supporting Documents</label>
                  <input
                    type="file"
                    multiple
                    className="file-input"
                    onChange={handleFileChange}
                  />
                  {selectedFiles.length > 0 && (
                    <p className="file-count">{selectedFiles.length} file(s) selected</p>
                  )}
                  {uploadProgress > 0 && uploadProgress < 100 && (
                    <div className="upload-progress">
                      <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
                      <span>{uploadProgress}%</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Terms */}
              <div className="form-section">
                <div className="form-group checkbox-single">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="agreeTerms"
                      checked={formData.agreeTerms}
                      onChange={handleChange}
                      required
                    />
                    I confirm that the information provided is accurate and agree to the terms and conditions. *
                  </label>
                </div>
              </div>

              <button type="submit" className="btn-gold submit-btn" disabled={loading}>
                {loading ? 'Submitting...' : 'Submit Partnership Inquiry'}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PartnershipCollaboration;